import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Save, Plus, Trash2, ClipboardCheck, Upload, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";

const apiBase = `${BACKEND_URL || ""}/api`;

const CAUSAS_DEFAULT = [
  "Falha de movimentação",
  "Divergência no recebimento",
  "Devolução não processada",
  "Ajuste de estoque não realizado",
  "Erro de contagem",
  "Integração de nota fiscal",
  "Retirada de provadores",
  "Bipagem de sacolas",
  "D-PARA",
  "Nota fiscal divergente",
  "Transferências entre lojas",
  "Outros",
];

const TOTALIZADORES = [
  ["tot_validados", "Total de itens validados"],
  ["tot_faltando", "Total de itens faltando"],
  ["tot_sobrando", "Total de itens sobrando"],
  ["tot_contagem_zero", "Total de itens contagem zero"],
  ["tot_consumo", "Total de itens de consumo"],
];

const fmtBRL = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
const fmtNum = (v) => (v === "" || v == null ? "" : new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v) || 0));

const EMPTY_TOT = { valor_estoque: "", valor_contagem: "" };

const EMPTY = {
  acuracidade_manual: "",
  custo_manual: "",
  analise_evolucao: "",
  tot_validados: { ...EMPTY_TOT },
  tot_faltando: { ...EMPTY_TOT },
  tot_sobrando: { ...EMPTY_TOT },
  tot_contagem_zero: { ...EMPTY_TOT },
  tot_consumo: { ...EMPTY_TOT },
  top3: [{ codigo: "", produto: "", sistema: "", contagem: "", divergencia: "", impacto: "" }, { codigo: "", produto: "", sistema: "", contagem: "", divergencia: "", impacto: "" }, { codigo: "", produto: "", sistema: "", contagem: "", divergencia: "", impacto: "" }],
  itens_criticos: [],
  causas: [],
  detalhamento_causas: "",
  oportunidades: "",
  destaques_positivos: "",
  plano_acao: [],
  consideracoes: "",
  observacoes: "",
  evidencias: [],
};

const inputCls = "w-full bg-white border border-[#D9E3DC] rounded px-2 py-1.5 text-sm text-[#0F2A1F] focus:outline-none focus:ring-2 focus:ring-[#0F6B3A]";
const labelCls = "text-[10px] font-bold text-[#5A6B62] uppercase tracking-wide";

export default function AcompanhamentoInventario({ mes, loja, snapshot, meta, canEdit = true, lojasDisponiveis = [] }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [lojaImport, setLojaImport] = useState(loja);
  const fileImportRef = useRef(null);
  const evidRef = useRef(null);
  const [uploadingEvid, setUploadingEvid] = useState(false);

  const uploadEvidencias = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingEvid(true);
    try {
      const paths = [];
      for (const f of Array.from(files)) {
        if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: acima de 10MB`); continue; }
        const fd = new FormData();
        fd.append("file", f);
        const { data: r } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(r.path);
      }
      if (paths.length) {
        setData((d) => ({ ...d, evidencias: [...(d.evidencias || []), ...paths] }));
        toast.success(`${paths.length} evidência(s) anexada(s)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao anexar evidência");
    } finally {
      setUploadingEvid(false);
      if (evidRef.current) evidRef.current.value = "";
    }
  };

  const removeEvidencia = (path) => {
    if (!window.confirm("Remover esta evidência do acompanhamento?")) return;
    setData((d) => ({ ...d, evidencias: (d.evidencias || []).filter((p) => p !== path) }));
  };

  useEffect(() => { setLojaImport(loja); }, [loja]);

  // Acuracidade/custo efetivos — usa manual se preenchido, senão o snapshot (matriz)
  const acur = useMemo(() => {
    if (data.acuracidade_manual !== "" && data.acuracidade_manual != null) return Number(data.acuracidade_manual);
    return snapshot?.acuracidade ?? null;
  }, [data.acuracidade_manual, snapshot]);
  const cst = useMemo(() => {
    if (data.custo_manual !== "" && data.custo_manual != null) return Number(data.custo_manual);
    return snapshot?.custo ?? null;
  }, [data.custo_manual, snapshot]);
  const status = useMemo(() => {
    if (acur == null) return { label: "—", cls: "text-[#5A6B62] bg-[#F2F5F3]" };
    if (acur >= meta) return { label: "Dentro da Meta", cls: "text-[#0F6B3A] bg-[#EAF7EF]" };
    if (acur >= meta - 3) return { label: "Em Atenção", cls: "text-[#9A6B0A] bg-[#FDF6E3]" };
    return { label: "Abaixo da Meta", cls: "text-[#B91C1C] bg-[#FDEDED]" };
  }, [acur, meta]);

  // Totalizadores auto-calculados
  const linhasTot = useMemo(() => TOTALIZADORES.map(([k, label]) => {
    const t = data[k] || EMPTY_TOT;
    const ve = t.valor_estoque === "" || t.valor_estoque == null ? 0 : Number(t.valor_estoque);
    const vc = t.valor_contagem === "" || t.valor_contagem == null ? 0 : Number(t.valor_contagem);
    return { key: k, label, ve, vc, saldo: vc - ve };
  }), [data]);
  const totais = useMemo(() => linhasTot.reduce((a, l) => ({ ve: a.ve + l.ve, vc: a.vc + l.vc }), { ve: 0, vc: 0 }), [linhasTot]);

  useEffect(() => {
    if (!mes || !loja) return;
    setLoading(true);
    api.get(`/inventario/acompanhamento`, { params: { mes, loja } })
      .then((r) => {
        const d = r.data;
        setData({
          ...EMPTY,
          ...d,
          acuracidade_manual: d.acuracidade_manual ?? "",
          custo_manual: d.custo_manual ?? "",
          tot_validados: d.tot_validados || EMPTY_TOT,
          tot_faltando: d.tot_faltando || EMPTY_TOT,
          tot_sobrando: d.tot_sobrando || EMPTY_TOT,
          tot_contagem_zero: d.tot_contagem_zero || EMPTY_TOT,
          tot_consumo: d.tot_consumo || EMPTY_TOT,
          top3: d.top3?.length ? d.top3 : EMPTY.top3,
        });
      })
      .catch(() => setData(EMPTY))
      .finally(() => setLoading(false));
  }, [mes, loja]);

  const salvar = async () => {
    setSaving(true);
    try {
      const clean = (v) => (v === "" || v == null ? null : Number(v));
      await api.post(`/inventario/acompanhamento`, {
        mes, loja,
        ...data,
        acuracidade_manual: clean(data.acuracidade_manual),
        custo_manual: clean(data.custo_manual),
        tot_validados: { valor_estoque: clean(data.tot_validados?.valor_estoque), valor_contagem: clean(data.tot_validados?.valor_contagem) },
        tot_faltando: { valor_estoque: clean(data.tot_faltando?.valor_estoque), valor_contagem: clean(data.tot_faltando?.valor_contagem) },
        tot_sobrando: { valor_estoque: clean(data.tot_sobrando?.valor_estoque), valor_contagem: clean(data.tot_sobrando?.valor_contagem) },
        tot_contagem_zero: { valor_estoque: clean(data.tot_contagem_zero?.valor_estoque), valor_contagem: clean(data.tot_contagem_zero?.valor_contagem) },
        tot_consumo: { valor_estoque: clean(data.tot_consumo?.valor_estoque), valor_contagem: clean(data.tot_consumo?.valor_contagem) },
      });
      toast.success("Acompanhamento salvo");
    } catch { toast.error("Erro ao salvar acompanhamento"); }
    finally { setSaving(false); }
  };

  const toggleCausa = (c) => setData((d) => ({ ...d, causas: d.causas.includes(c) ? d.causas.filter((x) => x !== c) : [...d.causas, c] }));
  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));

  // Importação do relatório final do inventário (CSV) — extrai a seção "Totalizadores - Financeiro (Custo)"
  const importarRelatorio = async (file) => {
    if (!file) return;
    if (!lojaImport) { toast.error("Selecione a loja de destino da importação"); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mes", mes);
      fd.append("loja", lojaImport);
      const { data: resp } = await api.post("/inventario/acompanhamento/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Relatório importado (${resp.categorias_importadas?.length || 0} categorias · saldo ${new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(resp.custo_total || 0)})`);
      if (lojaImport === loja) {
        // recarrega os dados do formulário
        const r = await api.get(`/inventario/acompanhamento`, { params: { mes, loja } });
        const d = r.data;
        setData({ ...EMPTY, ...d, acuracidade_manual: d.acuracidade_manual ?? "", custo_manual: d.custo_manual ?? "",
                  tot_validados: d.tot_validados || EMPTY_TOT, tot_faltando: d.tot_faltando || EMPTY_TOT,
                  tot_sobrando: d.tot_sobrando || EMPTY_TOT, tot_contagem_zero: d.tot_contagem_zero || EMPTY_TOT,
                  tot_consumo: d.tot_consumo || EMPTY_TOT, top3: d.top3?.length ? d.top3 : EMPTY.top3 });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao importar relatório");
    } finally {
      setImporting(false);
      if (fileImportRef.current) fileImportRef.current.value = "";
    }
  };

  const setTot = (cat, campo, v) => setData((d) => ({ ...d, [cat]: { ...(d[cat] || EMPTY_TOT), [campo]: v } }));
  const setTop = (i, k, v) => setData((d) => ({ ...d, top3: d.top3.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)) }));
  const addCritico = () => setData((d) => ({ ...d, itens_criticos: [...d.itens_criticos, { codigo: "", descricao: "", divergencia: "", motivo: "", acao: "", responsavel: "", prazo: "", status: "Pendente" }] }));
  const setCritico = (i, k, v) => setData((d) => ({ ...d, itens_criticos: d.itens_criticos.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)) }));
  const delCritico = (i) => setData((d) => ({ ...d, itens_criticos: d.itens_criticos.filter((_, idx) => idx !== i) }));
  const addAcao = () => setData((d) => ({ ...d, plano_acao: [...d.plano_acao, { acao: "", responsavel: "", prazo: "", status: "Pendente" }] }));
  const setAcao = (i, k, v) => setData((d) => ({ ...d, plano_acao: d.plano_acao.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)) }));
  const delAcao = (i) => setData((d) => ({ ...d, plano_acao: d.plano_acao.filter((_, idx) => idx !== i) }));

  return (
    <section data-testid="acompanhamento-card" className="bg-white border border-[#E3ECE6] rounded-xl shadow-sm">
      {/* Header (accordion) */}
      <button onClick={() => setOpen((o) => !o)} data-testid="acompanhamento-toggle"
        className="w-full flex flex-wrap items-center gap-3 px-5 py-4 text-left hover:bg-[#F4F8F5] transition-colors rounded-t-xl">
        <ClipboardCheck className="w-5 h-5 text-[#0F6B3A]" />
        <span className="text-lg font-semibold text-[#0F6B3A]">Acompanhamento do Inventário</span>
        <span className="text-sm text-[#5A6B62]">— {mes} · {loja}</span>
        {!open && (
          <span className="ml-auto flex flex-wrap items-center gap-2 text-xs">
            <span className="text-[#5A6B62]">Acuracidade:</span>
            <strong className="text-[#0F6B3A]">{acur != null ? `${acur.toFixed(2)}%` : "—"}</strong>
            <span className="text-[#5A6B62]">· Meta:</span>
            <strong className="text-[#0F6B3A]">{meta}%</strong>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold ${status.cls}`}>{status.label}</span>
            <span className="text-[#5A6B62]">· Divergência:</span>
            <strong className="text-[#2DC26B]">{cst != null ? fmtBRL(cst) : "—"}</strong>
          </span>
        )}
        {open ? <ChevronUp className="w-5 h-5 text-[#0F6B3A] ml-auto" /> : <ChevronDown className="w-5 h-5 text-[#0F6B3A]" />}
      </button>

      {open && (
        <div className="border-t border-[#E3ECE6] p-5 space-y-6" data-testid="acompanhamento-body">
          {loading && <div className="text-sm text-[#5A6B62]">Carregando...</div>}

          {/* 1. Resultado — agora com inputs editáveis para Acuracidade e Custo */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-lg border border-[#D9E3DC] p-3">
              <p className={labelCls}>Acuracidade (%)</p>
              <input type="number" step="0.01" data-testid="acomp-acuracidade" className={inputCls + " text-xl font-extrabold text-[#0F6B3A]"} placeholder={snapshot?.acuracidade != null ? `Matriz: ${snapshot.acuracidade.toFixed(2)}%` : "—"}
                value={data.acuracidade_manual} onChange={(e) => setField("acuracidade_manual", e.target.value)} disabled={!canEdit} />
              {snapshot?.acuracidade != null && data.acuracidade_manual !== "" && (
                <p className="text-[10px] text-[#8A9A90] mt-1">Matriz: {snapshot.acuracidade.toFixed(2)}%</p>
              )}
            </div>
            <div className="rounded-lg border border-[#D9E3DC] p-3">
              <p className={labelCls}>Meta</p>
              <p className="text-xl font-extrabold text-[#0F6B3A]">{meta}%</p>
            </div>
            <div className="rounded-lg border border-[#D9E3DC] p-3">
              <p className={labelCls}>Custo Divergência (R$)</p>
              <input type="number" step="0.01" data-testid="acomp-custo" className={inputCls + " text-xl font-extrabold text-[#2DC26B]"} placeholder={snapshot?.custo != null ? `Matriz: ${fmtBRL(snapshot.custo)}` : "—"}
                value={data.custo_manual} onChange={(e) => setField("custo_manual", e.target.value)} disabled={!canEdit} />
              {snapshot?.custo != null && data.custo_manual !== "" && (
                <p className="text-[10px] text-[#8A9A90] mt-1">Matriz: {fmtBRL(snapshot.custo)}</p>
              )}
            </div>
            <div className="rounded-lg border border-[#D9E3DC] p-3"><p className={labelCls}>Status</p><p className={`text-sm font-bold px-2 py-1 rounded-full inline-block ${status.cls}`}>{status.label}</p></div>
          </div>

          {/* 2. Análise da evolução */}
          <div>
            <p className={labelCls + " mb-1"}>Análise da Evolução</p>
            <textarea data-testid="acomp-analise" rows={2} className={inputCls} placeholder="Explique o motivo da evolução, estabilidade ou queda em relação ao inventário anterior..."
              value={data.analise_evolucao} onChange={(e) => setField("analise_evolucao", e.target.value)} disabled={!canEdit} />
          </div>

          {/* 3. Totalizadores — Financeiro (Custo) — TABELA idêntica ao print do relatório */}
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <h4 className="text-sm font-bold text-[#0F6B3A]">Totalizadores — Financeiro (Custo)</h4>
              {canEdit && (
                <div className="flex flex-wrap items-center gap-2" data-testid="acomp-import-bar">
                  <label className="text-[10px] font-bold text-[#5A6B62] uppercase">Importar para</label>
                  <select data-testid="acomp-import-loja" value={lojaImport} onChange={(e) => setLojaImport(e.target.value)}
                    className="text-xs bg-white border border-[#D9E3DC] rounded-md px-2 py-1.5 text-[#0F2A1F] focus:outline-none focus:ring-2 focus:ring-[#0F6B3A]">
                    {(lojasDisponiveis?.length ? lojasDisponiveis : [loja]).map((l) => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                  <input ref={fileImportRef} type="file" accept=".csv,text/csv" className="hidden"
                    onChange={(e) => importarRelatorio(e.target.files?.[0])} data-testid="acomp-import-file" />
                  <button data-testid="acomp-import-btn" onClick={() => fileImportRef.current?.click()} disabled={importing}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#0F6B3A] text-white hover:bg-[#0B5530] disabled:opacity-50 text-xs font-bold transition-colors">
                    <Upload className="w-3.5 h-3.5" /> {importing ? "Importando..." : "Importar Relatório"}
                  </button>
                </div>
              )}
            </div>
            <div className="overflow-x-auto border border-[#E3ECE6] rounded-lg">
              <table className="w-full text-xs" data-testid="acomp-totalizadores">
                <thead>
                  <tr className="bg-[#0F6B3A] text-white">
                    <th className="px-3 py-2 text-left">Resultado</th>
                    <th className="px-3 py-2 text-right">Valor estoque</th>
                    <th className="px-3 py-2 text-right">Valor contagem</th>
                    <th className="px-3 py-2 text-right">Saldo</th>
                  </tr>
                </thead>
                <tbody>{linhasTot.map(({ key, label, ve, vc, saldo }) => (
                  <tr key={key} className="border-t border-[#E3ECE6] hover:bg-[#F4F8F5]">
                    <td className="px-3 py-2 text-[#0F2A1F]">{label}</td>
                    <td className="px-1 py-1"><input type="number" step="0.01" data-testid={`tot-${key}-estoque`} className={inputCls + " text-right"}
                      value={data[key]?.valor_estoque ?? ""} onChange={(e) => setTot(key, "valor_estoque", e.target.value)} disabled={!canEdit} /></td>
                    <td className="px-1 py-1"><input type="number" step="0.01" data-testid={`tot-${key}-contagem`} className={inputCls + " text-right"}
                      value={data[key]?.valor_contagem ?? ""} onChange={(e) => setTot(key, "valor_contagem", e.target.value)} disabled={!canEdit} /></td>
                    <td className={`px-3 py-2 text-right font-bold ${saldo < 0 ? "text-[#B91C1C]" : saldo > 0 ? "text-[#0F6B3A]" : "text-[#5A6B62]"}`}>
                      {fmtNum(saldo) || "0,00"}
                    </td>
                  </tr>
                ))}
                  <tr className="bg-[#EAF7EF] font-bold text-[#0F2A1F] border-t-2 border-[#0F6B3A]">
                    <td className="px-3 py-2">Total</td>
                    <td className="px-3 py-2 text-right">{fmtNum(totais.ve) || "0,00"}</td>
                    <td className="px-3 py-2 text-right">{fmtNum(totais.vc) || "0,00"}</td>
                    <td className={`px-3 py-2 text-right ${totais.vc - totais.ve < 0 ? "text-[#B91C1C]" : "text-[#0F6B3A]"}`}>{fmtNum(totais.vc - totais.ve) || "0,00"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-[#8A9A90] mt-1">Saldo = Valor contagem − Valor estoque. Preenchimento automático do total.</p>
          </div>

          {/* 4. TOP 3 divergências */}
          <div>
            <h4 className="text-sm font-bold text-[#0F6B3A] mb-2">TOP 3 — Maiores Divergências</h4>
            <div className="overflow-x-auto"><table className="w-full text-xs border border-[#E3ECE6]">
              <thead className="bg-[#F4F8F5]"><tr>
                <th className="p-2 text-left whitespace-nowrap">#</th><th className="p-2 text-left whitespace-nowrap">Código</th><th className="p-2 text-left whitespace-nowrap">Produto</th><th className="p-2 text-left whitespace-nowrap">Sistema</th><th className="p-2 text-left whitespace-nowrap">Contagem</th><th className="p-2 text-left whitespace-nowrap">Divergência</th>
              </tr></thead>
              <tbody>{data.top3.map((r, i) => (
                <tr key={i} className="border-t border-[#E3ECE6]">
                  <td className="p-1 font-bold text-[#0F6B3A]">{i + 1}º</td>
                  {["codigo", "produto", "sistema", "contagem", "divergencia"].map((k) => (
                    <td key={k} className="p-1"><input className={inputCls} value={r[k] || ""} onChange={(e) => setTop(i, k, e.target.value)} disabled={!canEdit} /></td>
                  ))}
                </tr>
              ))}</tbody>
            </table></div>
          </div>

          {/* 5. Itens críticos */}
          <div>
            <div className="flex items-center justify-between mb-2"><h4 className="text-sm font-bold text-[#0F6B3A]">Itens Críticos</h4>
              {canEdit && <button onClick={addCritico} data-testid="acomp-add-critico" className="text-xs text-[#0F6B3A] hover:text-[#0B5530] font-bold inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Adicionar</button>}
            </div>
            {data.itens_criticos.length === 0 ? (
              <p className="text-xs text-[#8A9A90]">Nenhum item crítico registrado.</p>
            ) : (
              <div className="space-y-2">{data.itens_criticos.map((it, i) => (
                <div key={i} className="grid grid-cols-2 md:grid-cols-9 gap-1 bg-[#F4F8F5] rounded p-2 items-center">
                  {[["codigo","Código"],["descricao","Descrição"],["divergencia","Divergência"],["motivo","Motivo"],["acao","Ação"],["responsavel","Responsável"],["prazo","Prazo"]].map(([k,ph]) => (
                    <input key={k} placeholder={ph} className={inputCls} value={it[k] || ""} onChange={(e) => setCritico(i, k, e.target.value)} disabled={!canEdit} />
                  ))}
                  <select className={inputCls} value={it.status || "Pendente"} onChange={(e) => setCritico(i, "status", e.target.value)} disabled={!canEdit}>
                    <option>Pendente</option><option>Em andamento</option><option>Concluído</option>
                  </select>
                  {canEdit && <button onClick={() => delCritico(i)} className="text-[#B91C1C] hover:text-[#7f1d1d]"><Trash2 className="w-4 h-4" /></button>}
                </div>
              ))}</div>
            )}
          </div>

          {/* 6. Causas */}
          <div>
            <h4 className="text-sm font-bold text-[#0F6B3A] mb-2">Principais Causas Identificadas</h4>
            <div className="flex flex-wrap gap-2">{CAUSAS_DEFAULT.map((c) => (
              <button key={c} onClick={() => canEdit && toggleCausa(c)} data-testid={`acomp-causa-${c.substring(0,10)}`}
                className={`px-3 py-1 rounded-full text-xs border transition-colors ${data.causas.includes(c) ? "bg-[#0F6B3A] text-white border-[#0F6B3A]" : "bg-white text-[#0F2A1F] border-[#D9E3DC] hover:border-[#0F6B3A]"} ${!canEdit ? "opacity-60 cursor-not-allowed" : ""}`}>
                {c}
              </button>
            ))}</div>
            <textarea data-testid="acomp-detalhamento" rows={2} className={inputCls + " mt-2"} placeholder="Detalhamento das causas..." value={data.detalhamento_causas} onChange={(e) => setField("detalhamento_causas", e.target.value)} disabled={!canEdit} />
          </div>

          {/* 7. Oportunidades */}
          <div>
            <p className={labelCls + " mb-1"}>Principais Oportunidades de Melhoria</p>
            <textarea data-testid="acomp-oportunidades" rows={3} className={inputCls} placeholder="Melhorar endereçamento, revisar armazenamento, criar rotina de recontagem..." value={data.oportunidades} onChange={(e) => setField("oportunidades", e.target.value)} disabled={!canEdit} />
          </div>

          {/* 8. Destaques positivos */}
          <div>
            <p className={labelCls + " mb-1"}>Destaques Positivos</p>
            <textarea data-testid="acomp-destaques" rows={3} className={inputCls} placeholder="Boa organização do estoque, evolução da acuracidade, boa execução da equipe..." value={data.destaques_positivos} onChange={(e) => setField("destaques_positivos", e.target.value)} disabled={!canEdit} />
          </div>

          {/* 9. Plano de ação */}
          <div>
            <div className="flex items-center justify-between mb-2"><h4 className="text-sm font-bold text-[#0F6B3A]">Plano de Ação</h4>
              {canEdit && <button onClick={addAcao} data-testid="acomp-add-acao" className="text-xs text-[#0F6B3A] hover:text-[#0B5530] font-bold inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Adicionar</button>}
            </div>
            {data.plano_acao.length === 0 ? (
              <p className="text-xs text-[#8A9A90]">Nenhuma ação cadastrada.</p>
            ) : (
              <div className="space-y-2">{data.plano_acao.map((a, i) => (
                <div key={i} className="grid grid-cols-2 md:grid-cols-5 gap-1 bg-[#F4F8F5] rounded p-2 items-center">
                  {[["acao","Ação"],["responsavel","Responsável"],["prazo","Prazo"]].map(([k,ph]) => (
                    <input key={k} placeholder={ph} className={inputCls} value={a[k] || ""} onChange={(e) => setAcao(i, k, e.target.value)} disabled={!canEdit} />
                  ))}
                  <select className={inputCls} value={a.status || "Pendente"} onChange={(e) => setAcao(i, "status", e.target.value)} disabled={!canEdit}>
                    <option>Pendente</option><option>Em andamento</option><option>Concluído</option>
                  </select>
                  {canEdit && <button onClick={() => delAcao(i)} className="text-[#B91C1C] hover:text-[#7f1d1d]"><Trash2 className="w-4 h-4" /></button>}
                </div>
              ))}</div>
            )}
          </div>

          {/* 10. Considerações */}
          <div>
            <p className={labelCls + " mb-1"}>Considerações sobre o Inventário</p>
            <textarea data-testid="acomp-consideracoes" rows={4} className={inputCls} placeholder="Análise geral do inventário..." value={data.consideracoes} onChange={(e) => setField("consideracoes", e.target.value)} disabled={!canEdit} />
          </div>

          {/* 12. Observações */}
          <div>
            <p className={labelCls + " mb-1"}>Observações Gerais</p>
            <textarea data-testid="acomp-observacoes" rows={2} className={inputCls} value={data.observacoes} onChange={(e) => setField("observacoes", e.target.value)} disabled={!canEdit} />
          </div>

          {/* 11. Evidências (imagens comprobatórias) */}
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <h4 className="text-sm font-bold text-[#0F6B3A]">Evidências do Inventário</h4>
              {canEdit && (
                <>
                  <input ref={evidRef} type="file" accept="image/*,application/pdf" multiple className="hidden"
                    data-testid="acomp-evid-file" onChange={(e) => uploadEvidencias(e.target.files)} />
                  <button data-testid="acomp-evid-btn" onClick={() => evidRef.current?.click()} disabled={uploadingEvid}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white border border-[#0F6B3A] text-[#0F6B3A] hover:bg-[#EAF7EF] disabled:opacity-50 text-xs font-bold transition-colors">
                    <ImageIcon className="w-3.5 h-3.5" /> {uploadingEvid ? "Enviando..." : "Anexar imagens"}
                  </button>
                </>
              )}
            </div>
            {(!data.evidencias || data.evidencias.length === 0) ? (
              <p className="text-xs text-[#8A9A90]">Nenhuma evidência anexada. Aceita JPG/PNG/PDF · até 10MB por arquivo.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {data.evidencias.map((path, i) => {
                  const url = `${apiBase}/files/${path}`;
                  const isPdf = path.toLowerCase().endsWith(".pdf");
                  return (
                    <div key={path} className="relative group border border-[#E3ECE6] rounded-lg overflow-hidden bg-[#F4F8F5]">
                      {isPdf ? (
                        <a href={url} target="_blank" rel="noreferrer" className="flex items-center justify-center h-28 text-xs font-bold text-[#B91C1C]" data-testid={`acomp-evid-item-${i}`}>
                          📄 PDF
                        </a>
                      ) : (
                        <a href={url} target="_blank" rel="noreferrer" data-testid={`acomp-evid-item-${i}`}>
                          <img src={url} alt={`Evidência ${i + 1}`} className="w-full h-28 object-cover" />
                        </a>
                      )}
                      {canEdit && (
                        <button onClick={() => removeEvidencia(path)} data-testid={`acomp-evid-remove-${i}`}
                          className="absolute top-1 right-1 bg-[#B91C1C] text-white rounded-full p-1 shadow opacity-0 group-hover:opacity-100 hover:bg-[#7f1d1d] transition-opacity" title="Remover">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 13. Salvar */}
          {canEdit && (
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E3ECE6]">
              <button data-testid="acomp-salvar" onClick={salvar} disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0F6B3A] text-white hover:bg-[#0B5530] disabled:opacity-50 text-sm font-bold">
                <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar Acompanhamento"}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
